import { createFileRoute } from '@tanstack/react-router';
import { useDeferredValue, useState } from 'react';
import { Clock, Filter, Search, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Idea } from '@/api/ideas/list-ideas';
import type { IdeaStatus, Priority } from '@/api/ideas/type';
import { useListIdeas } from '@/api/ideas/list-ideas';
import { useSearchIdeas } from '@/api/ideas/search-ideas';
import { useUpdateIdeaStatus } from '@/api/ideas/update-idea-status';
import { Card, CardContent } from '@/components/ui/card.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getPriorityColor, getPriorityLabel, getPriorityOrder } from '@/utils/roadmap-utils';
import { roadmapSearchSchema } from '@/utils/search-params';
import { IdeaCard } from '@/components/ideas/idea-card';
import { useOrganization } from '@/contexts/organization-context';
import { Input } from '@/components/ui/input.tsx';

export const Route = createFileRoute('/_authenticated/roadmap/')({
  validateSearch: roadmapSearchSchema,
  component: RouteComponent
});

function RouteComponent() {
  const { projectId, organizationId } = useOrganization();
  const { data: ideasData, isLoading, isError } = useListIdeas(organizationId!, projectId!);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const { data: searchData } = useSearchIdeas(organizationId!, projectId!, deferredSearchTerm);
  const updateIdeaStatus = useUpdateIdeaStatus();
  const [statusFilter, setStatusFilter] = useState<string>('Tous');
  const [priorityFilter, setPriorityFilter] = useState<string>('Tous');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');

  const ideas = deferredSearchTerm.length > 0 ? searchData?.ideas || [] : ideasData?.ideas || [];

  const filteredIdeas = ideas.filter((item) => {
    const statusMatch =
      statusFilter === 'Tous' ||
      (statusFilter === 'Pending' && item.status === 'Pending') ||
      (statusFilter === 'Réalisées' && item.status === 'Done') ||
      (statusFilter === 'Rejetées' && item.status === 'Discarded');

    const priorityMatch =
      priorityFilter === 'Tous' ||
      (priorityFilter === 'High' && item.priority === 'High') ||
      (priorityFilter === 'Medium' && item.priority === 'Medium') ||
      (priorityFilter === 'Low' && item.priority === 'Low');

    return statusMatch && priorityMatch;
  });

  const sortedIdeas = filteredIdeas.sort((a, b) => {
    const priorityDiff = getPriorityOrder(a.priority) - getPriorityOrder(b.priority);
    if (priorityDiff !== 0) return priorityDiff;

    const timeA = new Date(a.createdOn).getTime();
    const timeB = new Date(b.createdOn).getTime();
    return sortOrder === 'desc' ? timeB - timeA : timeA - timeB;
  });

  const groupedIdeas: Record<Priority, Array<Idea>> = {
    High: [],
    Medium: [],
    Low: []
  };

  sortedIdeas.forEach((idea) => {
    groupedIdeas[idea.priority].push(idea);
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Chargement de la roadmap...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Erreur lors du chargement de la roadmap. Veuillez réessayer plus tard.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-24 pb-20 px-4">
        <div className="container mx-auto max-w-7xl space-y-8">
          {/* Header */}
          <div className="space-y-4 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary">
              <Sparkles className="h-4 w-4" />
              {"Organisé par l'IA"}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">
              Journal{' '}
              <span className="bg-linear-to-r from-primary to-secondary bg-clip-text text-transparent">des idées</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl">
              {"Idées consolidées et priorisées automatiquement selon l'impact et les retours de la communauté"}
            </p>
          </div>

          {/* Search and Filters */}
          <Card className="border-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center ">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">Grouper par priorité :</span>
                    <Select value={priorityFilter} onValueChange={(value: string) => setPriorityFilter(value)}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tous">Tous</SelectItem>
                        <SelectItem value="High">Haute</SelectItem>
                        <SelectItem value="Medium">Moyenne</SelectItem>
                        <SelectItem value="Low">Basse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Filtrer par statut :</span>
                    </div>
                    <Select value={statusFilter} onValueChange={(value: string) => setStatusFilter(value)}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tous">Tous</SelectItem>
                        <SelectItem value="Pending">En attente</SelectItem>
                        <SelectItem value="Réalisées">Réalisées</SelectItem>
                        <SelectItem value="Rejetées">Rejetées</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Rechercher une idée par titre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Effacer la recherche"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className="gap-2"
                >
                  <Clock className="h-4 w-4" />
                  {sortOrder === 'desc' ? 'Plus récents' : 'Plus anciens'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Ideas Grouped by Priority */}
          <div className="space-y-8">
            {(['High', 'Medium', 'Low'] as Array<Priority>).map((priority) => {
              const items = groupedIdeas[priority];
              if (items.length === 0) return null;
              const priorityStyle = getPriorityColor(priority);
              const PriorityIcon = priorityStyle.icon;
              const priorityLabel = getPriorityLabel(priority);

              return (
                <div key={priority} className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${priorityStyle.bg} rounded-lg border-2 ${priorityStyle.border}`}>
                      <PriorityIcon className={`h-5 w-5 ${priorityStyle.text}`} />
                    </div>
                    <h2 className={`text-2xl font-bold ${priorityStyle.text}`}>Priorité {priorityLabel}</h2>
                    <Badge
                      variant="outline"
                      className={`${priorityStyle.bg} ${priorityStyle.text} ${priorityStyle.border}`}
                    >
                      {items.length} {items.length > 1 ? 'idées' : 'idée'}
                    </Badge>
                  </div>

                  <div className="flex flex-col gap-4">
                    {items.map((item, index) => {
                      const handleStatusChange = (ideaId: string, newStatus: IdeaStatus) => {
                        if (!organizationId || !projectId) return;
                        updateIdeaStatus.mutate(
                          {
                            organizationId,
                            projectId,
                            ideaId,
                            status: newStatus
                          },
                          {
                            onSuccess: () => {
                              if (newStatus === 'Done') {
                                toast.success('Idée marquée comme réalisée', {
                                  description: item.title
                                });
                              } else if (newStatus === 'Discarded') {
                                toast.success('Idée rejetée', {
                                  description: item.title
                                });
                              }
                            },
                            onError: () => {
                              toast.error('Erreur', {
                                description: "Impossible de mettre à jour le statut de l'idée"
                              });
                            }
                          }
                        );
                      };

                      return (
                        <IdeaCard
                          key={item.id}
                          idea={item}
                          index={index}
                          onStatusChange={handleStatusChange}
                          isUpdating={updateIdeaStatus.isPending}
                          showActions={true}
                          from="roadmap"
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
